import { compose } from "infrastructure/store";
import { ECommerceOnboardingApplication } from "consts/onboarding";
import { delay } from "util/support";
import { putECommerceBillingFinanceSteps } from "features/onboarding/api";
import type { OnboardingBankingAndFinance } from "features/onboarding/types";
import { useSimpleRequest } from "hooks";
import { FormAdapter } from "hooks/useForm";
import { ECommerce } from "ui/forms/Onboarding";
import {
    useFieldsStatuses,
    useRequestedChanges,
    useFieldsComments,
    useApplicationDetailContext,
    useStepStatus,
    useApplicationStatus
} from "ui/organizms/Onboarding/Applications/common";

export const useBankingAndFinance = () => {
    const { getTotalStepFields, getStepData, fetchStep } = useApplicationDetailContext();

    const totalStepFields = getTotalStepFields(ECommerceOnboardingApplication.BillingFinance);
    const data = getStepData<OnboardingBankingAndFinance>(ECommerceOnboardingApplication.BillingFinance);

    const {
        getStepStatus
    } = useStepStatus();

    const {
        updateApplicationStatus
    } = useApplicationStatus();

    const {
        withRequestedChangeFields,
        serializeRequestedChangeFields
    } = useRequestedChanges();

    const {
        withFieldsStatuses,
        serializeFieldsStatuses
    } = useFieldsStatuses();

    const {
        withFieldsComments,
        serializeFieldsComments
    } = useFieldsComments();

    const request = useSimpleRequest();

    const stepDataWithRequestedChangeFields = compose<OnboardingBankingAndFinance>(
        withRequestedChangeFields,
        withFieldsStatuses,
        withFieldsComments
    )(data);

    const formProps: ECommerce.BankingAndFinance.OnboardingBankingAndFinanceForm = {
        bootstrapData: {},
        initialValues: ECommerce.BankingAndFinance.getInitialValues(stepDataWithRequestedChangeFields),
        isBootstrapDataLoading: false,
        onSaveOrCreate: async ({
            accountHolderName,
            bankName,
            iban,
            swift,
            currency,
            avgTransactionSize,
            monthlyIncome,
            transactionVolume,
            proofFileKey,
            ...restFormValues
        }, helpers) => {
            const response = await request(() => putECommerceBillingFinanceSteps({
                userId: data.userId,
                accountHolderName,
                bankName,
                iban,
                swift: swift || null,
                currency: String(currency),
                avgTransactionSize,
                monthlyIncome,
                transactionVolume,
                proofFileKey: String(proofFileKey),
                requestedChangeFields: serializeRequestedChangeFields(restFormValues),
                fieldsStatuses: serializeFieldsStatuses(restFormValues),
                fieldsComments: serializeFieldsComments(restFormValues),
                status: getStepStatus(restFormValues, totalStepFields)
            }));

            helpers!.setTouched({});

            await delay(1000)
                .then(() => fetchStep(ECommerceOnboardingApplication.BillingFinance))
                .then(() => delay(1000))
                .then(updateApplicationStatus);

            return FormAdapter.fromResponse(response);
        }
    };

    return {
        ...formProps,
        ...ECommerce.BankingAndFinance.useForm(formProps),
        ...stepDataWithRequestedChangeFields
    };
};

export default useBankingAndFinance;
